import React from "react";
import { User, Heart, AlertCircle, Cake, MapPin } from "lucide-react";

/**
 * Personal + Emergency + Health summary on the member profile.
 * Empty fields are gracefully omitted; the whole section hides if everything
 * is empty (i.e., legacy member that hasn't gone through new onboarding).
 */
export default function MemberDetailsSection({ member }) {
  const personal = [
    { icon: Cake,    label: "DOB",     value: member.dob ? new Date(member.dob).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null },
    { icon: User,    label: "Gender",  value: member.gender ? prettyGender(member.gender) : null },
    { icon: MapPin,  label: "Address", value: member.address || null },
    { icon: User,    label: "Email",   value: member.email || null },
  ];

  const emergency = [
    { icon: User,    label: "Contact name",  value: member.emergency_contact_name },
    { icon: User,    label: "Contact phone", value: member.emergency_contact_phone },
    { icon: Heart,   label: "Blood group",   value: member.blood_group },
  ];

  const goals = [
    { label: "Fitness goal",      value: member.fitness_goal },
    { label: "Preferred class",   value: member.preferred_class },
  ];

  const hasPersonal  = personal.some(f => f.value);
  const hasEmergency = emergency.some(f => f.value) || !!member.medical_notes;
  const hasGoals     = goals.some(f => f.value);

  if (!hasPersonal && !hasEmergency && !hasGoals) return null;

  return (
    <section data-testid="member-details-section" className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {hasPersonal && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="personal-card">
          <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2 mb-3">
            <User className="w-4 h-4 text-muted-foreground" /> Personal
          </h3>
          <DetailGrid items={personal} />
        </div>
      )}
      {(hasEmergency || hasGoals) && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-4" data-testid="health-emergency-card">
          {hasEmergency && (
            <div>
              <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2 mb-3">
                <AlertCircle className="w-4 h-4 text-rose-500" /> Emergency &amp; health
              </h3>
              <DetailGrid items={emergency} />
              {member.medical_notes && (
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1">Medical notes</p>
                  <p className="text-xs text-foreground/85 leading-relaxed">{member.medical_notes}</p>
                </div>
              )}
            </div>
          )}
          {hasGoals && (
            <div className={hasEmergency ? "pt-4 border-t border-border" : ""}>
              <h3 className="font-display text-sm font-semibold text-foreground mb-2">Goals &amp; preferences</h3>
              <DetailGrid items={goals.map(g => ({ ...g, icon: null }))} />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function DetailGrid({ items }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5">
      {items.filter(i => i.value).map(({ icon: Icon, label, value }) => (
        <div key={label} className="min-w-0">
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-0.5 flex items-center gap-1">
            {Icon && <Icon className="w-2.5 h-2.5" />}{label}
          </dt>
          <dd className="text-xs text-foreground font-medium truncate" title={value}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function prettyGender(g) {
  return { male: "Male", female: "Female", other: "Other", prefer_not_to_say: "Prefer not to say" }[g] || g;
}
